import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type { Cart, CartMetadataInput } from "@server/gql/graphql";
import { updateCartMetadata as _updateCartMetadata } from "@server/requests/checkout/updateCartMetadata";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type UpdateCartMetadata = <T extends boolean = false>(
  metadata: CartMetadataInput,
  async?: T,
) => T extends true ? Promise<Cart | undefined> : void;

export default function useUpdateCartMetadata() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (metadata: CartMetadataInput) => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return _updateCartMetadata({
        metadata,
        locale,
        cartId,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);
      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const updateCartMetadata = useCallback(
    (metadata: CartMetadataInput, async = false) => {
      if (async) return mutateAsync(metadata);
      mutate(metadata);
    },
    [mutate, mutateAsync],
  );

  return {
    updateCartMetadata: updateCartMetadata as UpdateCartMetadata,
    isErrorUpdateCartMetadata: isError,
    isPendingUpdateCartMetadata: isPending,
    isSuccessUpdateCartMetadata: isSuccess,
  };
}
